import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function ExecutionQueueTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/rcm/execution?limit=50';
      if (statusFilter) url += `&status=${statusFilter}`;
      if (typeFilter) url += `&action_type=${typeFilter}`;
      const r = await api.get(url);
      setTasks(r.data.tasks || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (taskId, action) => {
    try {
      await api.post(`/rcm/execution/${taskId}/${action}`);
      load();
      if (selectedTask?.id === taskId) {
        const r = await api.get(`/rcm/execution/${taskId}`);
        setSelectedTask(r.data);
      }
    } catch(e) { alert(e.response?.data?.detail || `${action} failed`); }
  };

  const openDetail = async (taskId) => {
    try { const r = await api.get(`/rcm/execution/${taskId}`); setSelectedTask(r.data); }
    catch { setSelectedTask(null); }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700', in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };

  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const failedCount = tasks.filter(t => t.status === 'failed').length;

  return (
    <div className="space-y-4" data-testid="execution-queue-tab">
      {/* Summary strip */}
      <div className="flex gap-3">
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{pendingCount}</p><p className="text-[10px] text-muted-foreground uppercase">Pending</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{failedCount}</p><p className="text-[10px] text-muted-foreground uppercase">Failed</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold">{tasks.length}</p><p className="text-[10px] text-muted-foreground uppercase">Total</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <select className="text-xs border rounded px-2 py-1" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="exec-filter-status">
          <option value="">All statuses</option>
          {['pending','in_progress','completed','failed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="text-xs border rounded px-2 py-1" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="exec-filter-type">
          <option value="">All types</option>
          {['rebill','appeal','writeoff'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
      </div>

      {/* Task list */}
      <Card>
        <CardContent className="px-3 pb-3 pt-3">
          {tasks.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No execution tasks</p> : (
            <table className="w-full text-xs" data-testid="exec-queue-table">
              <thead><tr className="border-b text-left">
                <th className="py-1.5 font-medium text-muted-foreground">Type</th>
                <th className="py-1.5 font-medium text-muted-foreground">Source</th>
                <th className="py-1.5 font-medium text-muted-foreground">Status</th>
                <th className="py-1.5 font-medium text-muted-foreground">Vendor Ref</th>
                <th className="py-1.5 font-medium text-muted-foreground">Result</th>
                <th className="py-1.5 font-medium text-muted-foreground">Created</th>
                <th className="py-1.5 font-medium text-muted-foreground">Actions</th>
                <th></th>
              </tr></thead>
              <tbody>{tasks.map((t) => (
                <tr key={t.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(t.id)}>
                  <td className="py-1.5 capitalize font-medium">{t.action_type}</td>
                  <td className="py-1.5 text-muted-foreground">{t.source_type} <span className="font-mono text-[10px]">{t.source_id?.slice(0,8)}...</span></td>
                  <td className="py-1.5">
                    <span className={`text-[10px] font-medium rounded px-1.5 py-0.5 ${statusColors[t.status] || ''}`}>{t.status}</span>
                    {t.failure_type && <span className="ml-1 text-[9px] text-red-500">{t.failure_type}</span>}
                  </td>
                  <td className="py-1.5">{t.vendor_request_id ? <span className="font-mono text-[10px]">{t.vendor_request_id}</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="py-1.5 text-muted-foreground truncate max-w-[150px]">{t.result_summary || '—'}</td>
                  <td className="py-1.5 text-muted-foreground text-[10px]">{t.created_at?.slice(0,16).replace('T',' ')}</td>
                  <td className="py-1.5">
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      {t.status === 'pending' && (
                        <>
                          <Button size="sm" className="h-5 text-[10px] px-2 bg-indigo-600 text-white" onClick={() => doAction(t.id, 'execute')} data-testid={`eq-execute-${t.id?.slice(0,8)}`}>Execute</Button>
                          <Button size="sm" variant="ghost" className="h-5 text-[10px] px-2" onClick={() => doAction(t.id, 'cancel')}>Cancel</Button>
                        </>
                      )}
                      {t.status === 'failed' && (
                        <Button size="sm" variant="outline" className="h-5 text-[10px] px-2" onClick={() => doAction(t.id, 'retry')} data-testid={`eq-retry-${t.id?.slice(0,8)}`}>Retry</Button>
                      )}
                    </div>
                  </td>
                  <td className="py-1.5"><ChevronRight className="h-3 w-3 text-muted-foreground" /></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Slide */}
      <SlidePanel open={!!selectedTask} onClose={() => setSelectedTask(null)} title="Execution Task Detail">
        {selectedTask && (
          <>
            <Card><CardContent className="p-3 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-medium capitalize">{selectedTask.action_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className={`font-medium rounded px-1.5 py-0.5 ${statusColors[selectedTask.status] || ''}`}>{selectedTask.status}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span>{selectedTask.source_type} / <span className="font-mono">{selectedTask.source_id?.slice(0,12)}...</span></span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{selectedTask.created_at?.slice(0,16).replace('T',' ')}</span></div>
              {selectedTask.executed_at && <div className="flex justify-between"><span className="text-muted-foreground">Executed</span><span>{selectedTask.executed_at?.slice(0,16).replace('T',' ')}</span></div>}
              {selectedTask.vendor_request_id && <div className="flex justify-between"><span className="text-muted-foreground">Vendor Ref</span><span className="font-mono">{selectedTask.vendor_request_id}</span></div>}
              {selectedTask.failure_type && <div className="flex justify-between"><span className="text-muted-foreground">Failure</span><span className="text-red-600">{selectedTask.failure_type}</span></div>}
              {selectedTask.result_summary && <div className="flex justify-between"><span className="text-muted-foreground">Result</span><span>{selectedTask.result_summary}</span></div>}
            </CardContent></Card>

            {/* Transport Logs (6.5-B) */}
            {selectedTask.transport_logs?.length > 0 && (
              <Card className="border-indigo-200">
                <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-[10px] text-indigo-700 uppercase">Vendor Transport Logs</CardTitle></CardHeader>
                <CardContent className="px-3 pb-3 space-y-1.5">
                  {selectedTask.transport_logs.map((tl) => (
                    <div key={tl.id} className="border rounded p-2 text-[10px] space-y-0.5">
                      <div className="flex justify-between">
                        <span className="font-mono font-medium">{tl.vendor_request_id}</span>
                        <EnvironmentBadge env={tl.environment} />
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Status: <span className={tl.transport_status === 'completed' ? 'text-emerald-600' : 'text-red-600'}>{tl.transport_status}</span></span>
                        <span>{tl.request_timestamp?.slice(11,19)}</span>
                      </div>
                      {tl.failure_type && <div className="text-red-600">Failure: {tl.failure_type} — {tl.failure_detail}</div>}
                      {tl.response_metadata && (
                        <details className="mt-1">
                          <summary className="text-[9px] text-muted-foreground cursor-pointer">Raw response</summary>
                          <pre className="bg-gray-900 text-gray-100 rounded p-1.5 mt-1 text-[9px] max-h-[100px] overflow-auto">{JSON.stringify(tl.response_metadata, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Payload snapshot */}
            {selectedTask.payload_snapshot && (
              <Card className="border-blue-200 bg-blue-50/10">
                <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-[10px] text-blue-700 uppercase">Immutable Payload Snapshot</CardTitle></CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="bg-gray-900 text-gray-100 rounded p-2 text-[10px] font-mono max-h-[200px] overflow-auto">
                    <pre>{JSON.stringify(selectedTask.payload_snapshot.data, null, 2)}</pre>
                  </div>
                  <p className="text-[9px] text-muted-foreground mt-1">Hash: {selectedTask.payload_snapshot.payload_hash?.slice(0,20)}... | Created: {selectedTask.payload_snapshot.created_at?.slice(0,16)}</p>
                </CardContent>
              </Card>
            )}

            {/* Action history */}
            {selectedTask.action_history?.length > 0 && (
              <Card>
                <CardHeader className="pb-1 pt-3 px-3"><CardTitle className="text-xs">Action History</CardTitle></CardHeader>
                <CardContent className="px-3 pb-3 space-y-1">
                  {selectedTask.action_history.map((a) => (
                    <div key={a.id} className="text-xs border-b pb-1 last:border-0">
                      <div className="flex justify-between">
                        <span className="font-medium capitalize">{a.action}</span>
                        <span className="text-muted-foreground text-[10px]">{a.timestamp?.slice(0,16).replace('T',' ')}</span>
                      </div>
                      {a.details && Object.keys(a.details).length > 0 && (
                        <p className="text-[10px] text-muted-foreground">{JSON.stringify(a.details)}</p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {selectedTask.status === 'pending' && (
                <>
                  <Button size="sm" className="h-7 text-xs bg-indigo-600 text-white" onClick={() => { doAction(selectedTask.id, 'execute'); }} data-testid="detail-execute">Execute</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { doAction(selectedTask.id, 'cancel'); setSelectedTask(null); }}>Cancel</Button>
                </>
              )}
              {selectedTask.status === 'failed' && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doAction(selectedTask.id, 'retry')} data-testid="detail-retry">Retry (same payload)</Button>
              )}
            </div>
          </>
        )}
      </SlidePanel>
    </div>
  );
}

// ═══════════════════════════════════════
// OPERATIONS TAB (Phase 6 Slice 5)
// ═══════════════════════════════════════

export { ExecutionQueueTab };
